// ============================================
// MiBandeja.js — "Mi Trabajo": la bandeja personal (FLUJOS FIJOS, Entrega 1)
// ============================================
// Pieza central del sistema de 3 flujos fijos: al entrar al portal cada quien
// ve SU chamba sin buscar nada. Muestra mis expedientes pendientes ordenados
// por urgencia, en que paso van, QUE me pide el sistema en cada uno
// ("Falta: ...") y el boton que abre el expediente para resolverlo.
//
// Backend: GET /api/mi-trabajo (src/routes/flujos.js). Click en un item lleva
// a Expedientes con la ficha abierta (mismo patron state-based del MI-DIA-V2,
// NO react-router).

import React, { useState, useEffect, useCallback } from 'react';
import SolicitudesDatosPanel from './SolicitudesDatosPanel';
import DocumentosAvisosPanel from './DocumentosAvisosPanel';
import GarantiasAvisosPanel from './GarantiasAvisosPanel';
import CobroAvisosPanel from './CobroAvisosPanel';
import { useUsuario } from '../context/UsuarioContext';
import { storage, STORAGE_KEYS } from '../utils/storage';
import { ICONO_PASO } from '../utils/flujos';
import {
  Inbox, ChevronRight, Check, Lock, RefreshCw, AlertCircle, PartyPopper, Workflow,
} from 'lucide-react';
import { API_BASE } from '../config/api';


const FF = "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";

const URGENCIA_VISUAL = {
  vencido:     { color: '#dc2626', fondo: '#fef2f2', borde: '#fecaca', label: 'Vencido' },
  esta_semana: { color: '#b45309', fondo: '#fffbeb', borde: '#fde68a', label: 'Esta semana' },
  hoy:         { color: '#166534', fondo: '#f0fdf4', borde: '#bbf7d0', label: 'Hoy' },
};

function MiBandeja({ esCelular, onNavegar }) {
  const { usuario, accesoTotal, puedeAcceder } = useUsuario();
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [filtroUrgencia, setFiltroUrgencia] = useState(null); // null = todos

  // "Ver todos" en Mi Trabajo es supervision (bandeja de TODA la empresa, no la
  // personal). TRANSPARENCIA-TOTAL: la vista abierta a todos vive en Expedientes;
  // este switch queda gateado por la matriz granular (mitrabajo.vertodos), con el
  // bypass de direccion (accesoTotal). Asi es delegable desde Usuarios (politica
  // de permisos: ningun gate vive solo en el rol).
  const puedeVerTodos = accesoTotal || puedeAcceder('mitrabajo.vertodos');
  const [verTodos, setVerTodos] = useState(() => storage.getRaw(STORAGE_KEYS.MITRABAJO_VER_TODOS) === '1');
  const verTodosActivo = puedeVerTodos && verTodos;
  const cambiarVerTodos = () => {
    const nv = !verTodos;
    setVerTodos(nv);
    storage.set(STORAGE_KEYS.MITRABAJO_VER_TODOS, nv ? '1' : '0');
  };
  const toggleEl = puedeVerTodos ? <ToggleVerTodos activo={verTodosActivo} onToggle={cambiarVerTodos} /> : null;

  const cargarBandeja = useCallback(async () => {
    try {
      setError(null);
      const token = storage.getRaw(STORAGE_KEYS.AUTH_TOKEN);
      const url = API_BASE + '/api/mi-trabajo' + (verTodosActivo ? '?todos=1' : '');
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const json = await res.json();
      if (!json.ok) throw new Error(json.error || 'Error desconocido');
      setData(json);
    } catch (e) {
      console.error('[MiBandeja]', e);
      setError(e.message);
    } finally {
      setCargando(false);
    }
  }, [verTodosActivo]);

  useEffect(() => {
    if (!usuario || !usuario.id) return;
    setCargando(true);
    cargarBandeja();
  }, [usuario, cargarBandeja]);

  // Abrir la ficha del expediente. FLUJOS-FIJOS (hallazgo del arquitecto):
  // se usa el patron CO-LINK-EXPEDIENTE ya existente (CustomEvent global que
  // Dashboard escucha y traduce a la prop aperturaInicialId de Expedientes).
  // El storage.set de filtros solo limpia filtros pegados (intencion nueva).
  const abrirExpediente = (id) => {
    storage.set(STORAGE_KEYS.EXPEDIENTES_FILTROS, { responsableId: usuario.id });
    window.dispatchEvent(new CustomEvent('beep-abrir-expediente', { detail: { expedienteId: id } }));
  };

  const irAExpedientes = () => {
    storage.set(STORAGE_KEYS.EXPEDIENTES_FILTROS, { responsableId: usuario.id });
    if (onNavegar) onNavegar('operaciones.expedientes-todos');
  };

  // ===== ESTADOS DE LA VISTA =====
  if (cargando) {
    return (
      <div style={est.contenedor}>
        <div style={est.headerFila}>
          <TituloBandeja />{toggleEl}
        </div>
        <div style={{ display: 'grid', gap: 10, gridTemplateColumns: esCelular ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', marginBottom: 14 }}>
          {[1, 2, 3, 4].map(i => <div key={i} style={{ height: 72, background: '#f3f4f6', borderRadius: 10 }} />)}
        </div>
        {[1, 2, 3].map(i => <div key={i} style={{ height: 84, background: '#f8fafc', borderRadius: 10, marginBottom: 8 }} />)}
      </div>
    );
  }

  if (error) {
    return (
      <div style={est.contenedor}>
        <div style={est.headerFila}><TituloBandeja />{toggleEl}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 14, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, flexWrap: 'wrap' }}>
          <AlertCircle size={18} color="#dc2626" />
          <span style={{ fontSize: 13, color: '#64748b', flex: 1, minWidth: 160 }}>
            No se pudo cargar tu bandeja.
          </span>
          <button
            onClick={() => { setCargando(true); cargarBandeja(); }}
            style={{ ...est.botonSecundario, display: 'inline-flex', alignItems: 'center', gap: 6 }}
          ><RefreshCw size={14} /> Reintentar</button>
        </div>
      </div>
    );
  }

  if (!data || data.kpis.total === 0) {
    return (
      <div style={est.contenedor}>
        <div style={est.headerFila}><TituloBandeja />{toggleEl}</div>
        <div style={est.vacio}>
          <PartyPopper size={26} color="#94a3b8" strokeWidth={1.6} />
          <div style={{ marginTop: 8 }}>
            {verTodosActivo
              ? 'No hay expedientes pendientes en la empresa ahora mismo.'
              : 'No tienes expedientes pendientes ahora mismo. ¡Buen trabajo!'}
          </div>
        </div>
        {/* ALERTAS DE COBRO (AUDITOR Parte C): una deuda del cliente no se esconde porque la bandeja este vacia (visto en el celular). */}
        <div style={{ marginTop: 16 }}>
          <CobroAvisosPanel todos={verTodosActivo} compacto={esCelular} onAbrirExpediente={abrirExpediente} />
        </div>
      </div>
    );
  }

  const { kpis } = data;
  const items = filtroUrgencia
    ? data.items.filter(i => i.urgencia === filtroUrgencia)
    : data.items;

  return (
    <div style={est.contenedor}>
      <div style={est.headerFila}>
        <TituloBandeja total={kpis.total} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {toggleEl}
          <button style={est.botonVerTodos} onClick={irAExpedientes}>
            Ir a Expedientes {'→'}
          </button>
        </div>
      </div>

      {/* KPIs (tocarlos filtra la lista de abajo) */}
      <div style={{ display: 'grid', gap: 10, gridTemplateColumns: esCelular ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', marginBottom: 14 }}>
        <KpiCard
          activo={filtroUrgencia === null}
          numero={kpis.total} label="Pendientes" color="#185FA5" fondo="#eff6ff" borde="#bfdbfe"
          onClick={() => setFiltroUrgencia(null)}
        />
        <KpiCard
          activo={filtroUrgencia === 'vencido'}
          numero={kpis.vencidos} label="Vencidos" color="#dc2626" fondo="#fef2f2" borde="#fecaca"
          onClick={() => setFiltroUrgencia(filtroUrgencia === 'vencido' ? null : 'vencido')}
        />
        <KpiCard
          activo={filtroUrgencia === 'esta_semana'}
          numero={kpis.esta_semana} label="Esta semana" color="#b45309" fondo="#fffbeb" borde="#fde68a"
          onClick={() => setFiltroUrgencia(filtroUrgencia === 'esta_semana' ? null : 'esta_semana')}
        />
        <KpiCard
          activo={filtroUrgencia === 'hoy'}
          numero={kpis.hoy} label="Hoy" color="#166534" fondo="#f0fdf4" borde="#bbf7d0"
          onClick={() => setFiltroUrgencia(filtroUrgencia === 'hoy' ? null : 'hoy')}
        />
      </div>

      {/* LISTA: cada item = expediente + paso + que falta + accion */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {items.length === 0 && (
          <div style={est.vacio}>Nada en esta categoria.</div>
        )}
        {items.map(item => (
          <ItemBandeja key={item.id} item={item} esCelular={esCelular} mostrarResponsable={verTodosActivo} onAbrir={() => abrirExpediente(item.id)} />
        ))}
      </div>

      {/* GARANTIAS (Entrega 2): avisos de garantias por vencer del responsable comercial. */}
      <div style={{ marginTop: 16 }}>
        {/* DOCUMENTOS DE LA EMPRESA por vencer (2026-09-08): solo lo ve direccion/administracion (403 -> no se pinta). */}
        <DocumentosAvisosPanel />
        {/* PORTAL DEL CLIENTE (2026-09-08): clientes que completaron sus datos (responsable). */}
        <SolicitudesDatosPanel mios />
        {/* ALERTAS DE COBRO (AUDITOR Parte C, 2026-10-21): las del vendedor; con "Ver todos", las de toda la empresa. */}
        <CobroAvisosPanel todos={verTodosActivo} compacto={esCelular} onAbrirExpediente={abrirExpediente} />
        <GarantiasAvisosPanel mios esCelular={esCelular} onAbrirExpediente={abrirExpediente} />
      </div>
    </div>
  );
}

function TituloBandeja({ total }) {
  return (
    <h3 style={est.titulo}>
      <Inbox size={18} color="#185FA5" style={{ flexShrink: 0 }} />
      Mi Trabajo{typeof total === 'number' ? ` (${total})` : ''}
    </h3>
  );
}

function KpiCard({ numero, label, color, fondo, borde, onClick, activo }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '12px 10px', borderRadius: 10, cursor: 'pointer', textAlign: 'center',
        border: activo ? `2px solid ${color}` : `1px solid ${borde}`,
        background: fondo, transition: 'all 0.15s ease-out', fontFamily: FF,
        minHeight: 44, WebkitTapHighlightColor: 'transparent',
      }}
    >
      <div style={{ fontSize: 22, fontWeight: 700, color, marginBottom: 2 }}>{numero}</div>
      <div style={{ fontSize: 11.5, color: '#64748b', fontWeight: 600 }}>{label}</div>
    </button>
  );
}

function ItemBandeja({ item, esCelular, mostrarResponsable, onAbrir }) {
  const urg = URGENCIA_VISUAL[item.urgencia] || URGENCIA_VISUAL.hoy;
  const IconoPaso = ICONO_PASO[item.paso && item.paso.codigo] || Workflow;
  const colorPaso = (item.paso && item.paso.color) || '#94a3b8';
  const [presionado, setPresionado] = useState(false);

  const textoTiempo = item.dias_sin_movimiento === 0 ? 'hoy'
    : item.dias_sin_movimiento === 1 ? 'hace 1 dia'
    : `hace ${item.dias_sin_movimiento} dias`;

  return (
    <button
      onClick={onAbrir}
      onPointerDown={() => setPresionado(true)}
      onPointerUp={() => setPresionado(false)}
      onPointerLeave={() => setPresionado(false)}
      style={{
        width: '100%', textAlign: 'left', fontFamily: FF, cursor: 'pointer',
        background: '#fff', border: `1px solid ${presionado ? urg.borde : '#e2e8f0'}`,
        borderLeft: `4px solid ${urg.color}`,
        borderRadius: 10, padding: '12px 14px',
        transform: presionado ? 'scale(0.99)' : 'scale(1)',
        transition: 'all 0.15s ease-out',
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      {/* Fila 1: titulo + chip de paso */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{
          flex: 1, minWidth: 140, fontSize: 14, fontWeight: 700, color: '#1e293b',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{item.titulo || item.folio}</span>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 5,
          fontSize: 11, fontWeight: 700, color: colorPaso,
          background: colorPaso + '1a', border: `1px solid ${colorPaso}44`,
          padding: '3px 9px', borderRadius: 99, flexShrink: 0,
          textTransform: 'uppercase', letterSpacing: 0.3,
        }}>
          <IconoPaso size={12} />
          {(item.paso && (item.paso.nombre || item.paso.codigo)) || 'Sin paso'}
        </span>
      </div>

      {/* Fila 2: cliente + tiempo */}
      <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
        {item.cliente_nombre || 'Sin cliente'}
        {item.sucursal_nombre ? ` · ${item.sucursal_nombre}` : ''}
        {' · '}<span style={{ color: urg.color, fontWeight: 600 }}>{textoTiempo}</span>
      </div>

      {/* Responsable: solo en modo "Ver todos" (para saber de quien es el expediente) */}
      {mostrarResponsable && (
        <div style={{ fontSize: 11.5, color: '#64748b', marginTop: 3 }}>
          Responsable: <span style={{ fontWeight: 600, color: '#475569' }}>{item.responsable_nombre || 'Sin asignar'}</span>
        </div>
      )}

      {/* Fila 3: QUE me pide el sistema + accion */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, marginTop: 8,
        flexWrap: esCelular ? 'wrap' : 'nowrap',
      }}>
        <div style={{
          flex: 1, minWidth: esCelular ? '100%' : 200,
          display: 'flex', alignItems: 'flex-start', gap: 6,
          fontSize: 12.5, lineHeight: 1.4,
        }}>
          {item.fuera_de_flujo ? (
            <span style={{ color: '#94a3b8' }}>Fuera del flujo (direccion debe reubicarlo)</span>
          ) : item.listo_para_avanzar ? (
            <>
              <Check size={14} color="#166534" style={{ flexShrink: 0, marginTop: 1 }} />
              <span style={{ color: '#166534', fontWeight: 600 }}>Listo para avanzar al siguiente paso</span>
            </>
          ) : item.falta ? (
            <>
              <Lock size={13} color="#b45309" style={{ flexShrink: 0, marginTop: 2 }} />
              <span style={{ color: '#92400e', fontWeight: 500 }}>Falta: {item.falta}</span>
            </>
          ) : (
            <span style={{ color: '#94a3b8' }}>Sin pendiente registrado</span>
          )}
        </div>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0,
          fontSize: 12.5, fontWeight: 700, color: '#185FA5',
          padding: '8px 12px', minHeight: 36, borderRadius: 8,
          background: '#eff6ff', border: '1px solid #bfdbfe',
        }}>
          {item.listo_para_avanzar ? 'Continuar' : 'Resolver'} <ChevronRight size={14} />
        </span>
      </div>
    </button>
  );
}

// Switch "Ver todos" — solo direccion. Misma tabla, pero de toda la empresa.
function ToggleVerTodos({ activo, onToggle }) {
  return (
    <button
      onClick={onToggle}
      title="Ver los expedientes de toda la empresa"
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        background: activo ? '#eff6ff' : '#fff',
        border: `1px solid ${activo ? '#185FA5' : '#e2e8f0'}`,
        borderRadius: 99, padding: '7px 12px', cursor: 'pointer', fontFamily: FF, minHeight: 40,
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      <span style={{
        width: 32, height: 18, borderRadius: 99, position: 'relative', flexShrink: 0,
        background: activo ? '#185FA5' : '#cbd5e1', transition: 'background 0.15s',
      }}>
        <span style={{
          position: 'absolute', top: 2, left: activo ? 16 : 2, width: 14, height: 14, borderRadius: '50%',
          background: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,0.3)', transition: 'left 0.15s',
        }} />
      </span>
      <span style={{ fontSize: 12.5, fontWeight: 700, color: activo ? '#185FA5' : '#64748b' }}>Ver todos</span>
    </button>
  );
}

const est = {
  contenedor: {
    background: 'white', borderRadius: 12, padding: 18, marginBottom: 16,
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0',
    fontFamily: FF,
  },
  headerFila: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14,
    gap: 8, flexWrap: 'wrap',
  },
  titulo: {
    margin: 0, fontSize: 17, fontWeight: 700, color: '#1e293b',
    display: 'inline-flex', alignItems: 'center', gap: 8,
  },
  botonVerTodos: {
    background: 'transparent', border: 'none', color: '#5c9adb',
    fontSize: 13.5, fontWeight: 600, cursor: 'pointer', padding: '10px 12px',
    minHeight: 44, fontFamily: FF,
  },
  botonSecundario: {
    background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
    color: '#475569', fontSize: 13, fontWeight: 600, cursor: 'pointer',
    padding: '8px 14px', minHeight: 44, fontFamily: FF,
  },
  vacio: {
    textAlign: 'center', color: '#64748b', padding: 22, fontSize: 13.5,
    background: '#f8fafc', borderRadius: 10,
  },
};

export default MiBandeja;
